import { Toaster } from '@/components/ui/sonner';
import { DEFAULT_LANG, detectLang, isLang, LanguageProvider, useT } from '@/i18n';
import HomeScreen from '@/pages/HomeScreen';
import LearningScreen from '@/pages/LearningScreen';
import ModuleScreen from '@/pages/ModuleScreen';
import ModulesScreen from '@/pages/ModulesScreen';
import ProfileScreen from '@/pages/ProfileScreen';
import ProgressScreen from '@/pages/ProgressScreen';
import ResultsScreen from '@/pages/ResultsScreen';
import SplashScreen from '@/pages/SplashScreen';
import { useGameStore } from '@/store/useGameStore';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useParams } from 'react-router-dom';

const LoadingScreen = () => {
  const t = useT();
  return (
    <div className="min-h-screen gradient-warm-bg flex items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-xl gradient-primary-btn shadow-primary flex items-center justify-center animate-pulse-soft">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary-foreground"><path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2" /><path d="M15 18H9" /><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14" /><circle cx="17" cy="18" r="2" /><circle cx="7" cy="18" r="2" /></svg>
        </div>
        <p className="text-sm text-muted-foreground font-body">{t('ui.common.loading')}</p>
      </div>
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { lang } = useParams();
  const isRegistered = useGameStore((s) => s.isRegistered);
  const hasHydrated = useGameStore((s) => s._hasHydrated);

  if (!hasHydrated) return <LoadingScreen />;
  if (!isRegistered) return <Navigate to={`/${lang}`} replace />;
  return children;
};

/* Язык берётся из URL и нигде больше не хранится. Неизвестный код в адресе —
 * не ошибка для пользователя, а повод показать язык по умолчанию. */
const LangLayout = () => {
  const { lang } = useParams();
  if (!isLang(lang)) return <Navigate to={`/${DEFAULT_LANG}`} replace />;
  return (
    <LanguageProvider lang={lang}>
      <Outlet />
    </LanguageProvider>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/:lang" element={<LangLayout />}>
          <Route index element={<SplashScreen />} />
          <Route path="home" element={
            <ProtectedRoute><HomeScreen /></ProtectedRoute>
          } />
          <Route path="learn/:moduleId" element={
            <ProtectedRoute><LearningScreen /></ProtectedRoute>
          } />
          <Route path="module/:moduleId" element={
            <ProtectedRoute><ModuleScreen /></ProtectedRoute>
          } />
          <Route path="results/:attemptId" element={
            <ProtectedRoute><ResultsScreen /></ProtectedRoute>
          } />
          <Route path="modules" element={
            <ProtectedRoute><ModulesScreen /></ProtectedRoute>
          } />
          <Route path="progress" element={
            <ProtectedRoute><ProgressScreen /></ProtectedRoute>
          } />
          <Route path="profile" element={
            <ProtectedRoute><ProfileScreen /></ProtectedRoute>
          } />
        </Route>
        {/* голый "/" и любой мусор — на язык браузера, если он у нас есть */}
        <Route path="*" element={<Navigate to={`/${detectLang()}`} replace />} />
      </Routes>
      <Toaster position="top-center" />
    </BrowserRouter>
  );
}

export default App;
